<template>
    <RedesignBaseChart
        ref="chartRef"
        class="w-full h-full"
        :widget="widget"
        :option="option"
    />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import type { WidgetExtended } from '@/types/dashboard'
import type { ChartOptions } from '@/types/echart'
import { multiQueueColumnsPropNames } from '@/helpers/multiQueueDashboardRedesign'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import { activitiesToDisplayEnum, BASE_ACTIVITY_FONT, MetricsState } from '@/enum/redesignQueueDashboardStatistics'
import i18n from '@/i18n'

const props = defineProps<{
    widget: WidgetExtended
    metrics: MetricsState
}>()

const chartRef = ref<InstanceType<typeof RedesignBaseChart> | null>(null)
const chartSize = ref(300)
const chartEl = computed(() => chartRef.value?.$el as HTMLElement | undefined)

const hexOpacity = '30'

const outerRadius = computed(() => chartSize.value * 0.48)
const innerRadius = computed(() => chartSize.value * 0.36)

const ringWidth = computed(() =>
    Math.max(chartSize.value * 0.11, 10)
)

const answerPercent = computed(
    () => props.metrics[multiQueueColumnsPropNames.ANSWER]?.value ?? 0
)

const inSlaPercent = computed(
    () => props.metrics[multiQueueColumnsPropNames.IN_SLA_COUNT]?.value ?? 0
)

const answerColor = computed(() =>
    props.widget.WidgetLayout?.AnswerCount?.color ??
    props.metrics[multiQueueColumnsPropNames.ANSWER]?.color
)

const inSLAColor = computed(() =>
    props.widget.WidgetLayout?.InSLACount?.color ??
    props.metrics[multiQueueColumnsPropNames.IN_SLA_COUNT]?.color
)

const answerBg = computed(() => answerColor.value + hexOpacity)
const inSLABg = computed(() => inSLAColor.value + hexOpacity)

const answerBaseFont = computed(() =>
    props.widget.WidgetLayout?.AnswerCount?.fontSize ?? BASE_ACTIVITY_FONT
)

const inSLABaseFont = computed(() =>
    props.widget.WidgetLayout?.InSLACount?.fontSize ?? BASE_ACTIVITY_FONT
)

const answerFontSize = computed(() =>
    Math.max(answerBaseFont.value * (chartSize.value / 300), 20)
)

const inSLAFontSize = computed(() =>
    Math.max(inSLABaseFont.value * (chartSize.value / 300), 20)
)

const mainActivity = computed(() => {
    return props.widget.WidgetLayout?.mainActivity ?? activitiesToDisplayEnum.answerCount
})

const filteredSeries = computed(() => {
    if (!props.widget.WidgetLayout?.ShowActivities) {
        return [ activitiesToDisplayEnum.answerCount, activitiesToDisplayEnum.InSLACount ]
    }
    return props.widget.WidgetLayout?.ShowActivities
})

const answerChartOption = computed(() => {
    return {
        type: 'gauge',
        startAngle: 90,
        endAngle: -270,
        radius: radiusByActivity.value[activitiesToDisplayEnum.answerCount],
        pointer: { show: false },

        progress: {
            show: true,
            roundCap: true,
            width: ringWidth.value,
        },

        axisLine: {
            lineStyle: {
                width: ringWidth.value,
                color: [ [ 1, answerBg.value ] ],
            },
        },

        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        detail: { show: false },

        data: [
            {
                value: answerPercent.value,
                itemStyle: { color: answerColor.value },
            },
        ],
    }
})

const inSlaChartOption = computed(() => {
    return {
        type: 'gauge',
        startAngle: 90,
        endAngle: -270,
        radius: radiusByActivity.value[activitiesToDisplayEnum.InSLACount],
        pointer: { show: false },

        progress: {
            show: true,
            roundCap: true,
            width: ringWidth.value,
        },

        axisLine: {
            lineStyle: {
                width: ringWidth.value,
                color: [ [ 1, inSLABg.value ] ],
            },
        },

        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        detail: { show: false },

        data: [
            {
                value: inSlaPercent.value,
                itemStyle: { color: inSLAColor.value },
            },
        ],
    }
})
const seriesMap = computed(() => ({
    [activitiesToDisplayEnum.answerCount]: answerChartOption.value,
    [activitiesToDisplayEnum.InSLACount]: inSlaChartOption.value,
}))

const chartSeries = computed(() => {
    const visible = filteredSeries.value
        .map(key => seriesMap.value[key])
        .filter(Boolean)

    return visible.sort((a, b) => {
        return b.radius - a.radius
    })
})

const radiusByActivity = computed(() => {
    const main = mainActivity.value

    return {
        [activitiesToDisplayEnum.answerCount]:
            main === activitiesToDisplayEnum.answerCount
                ? outerRadius.value
                : innerRadius.value,

        [activitiesToDisplayEnum.InSLACount]:
            main === activitiesToDisplayEnum.InSLACount
                ? outerRadius.value
                : innerRadius.value,
    }
})


const option = computed<ChartOptions>(() => ({
    animationDurationUpdate: 300,

    series: chartSeries.value,
    graphic: {
        type: 'group',
        left: 'center',
        top: 'center',
        children: [
            {
                type: 'text',
                style: {
                    text: `${answerPercent.value}%`,
                    fontSize: answerFontSize.value,
                    fontWeight: 700,
                    fill: answerColor.value,
                    textAlign: 'center',
                },
                y: -chartSize.value * 0.08,
            },
            {
                type: 'text',
                style: {
                    text: i18n.t('queue.answer'),
                    fontSize: answerFontSize.value * 0.8,
                    fill: answerColor.value,
                    textAlign: 'center',
                },
                y: -chartSize.value * 0.01,
            },
            {
                type: 'text',
                style: {
                    text: `${inSlaPercent.value}%`,
                    fontSize: inSLAFontSize.value,
                    fontWeight: 600,
                    fill: inSLAColor.value,
                    textAlign: 'center',
                },
                y: chartSize.value * 0.07,
            },
            {
                type: 'text',
                style: {
                    text: i18n.t('queue.inSLA'),
                    fontSize: inSLAFontSize.value * 0.8,
                    fill: inSLAColor.value,
                    textAlign: 'center',
                },
                y: chartSize.value * 0.15,
            },
        ],
    },
}))

useResizeObserver(chartEl, (entries) => {
    const rect = entries[0]?.contentRect
    if (!rect) return

    chartSize.value = Math.min(rect.width, rect.height)
})
</script>
